import { usePriorityStore } from "@/stores/priorityStore";
import { Button, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Component that listens to priority store changes and triggers
 * contextual toast notifications at key moments.
 *
 * This component renders nothing - it only triggers side effects.
 */
function PriorityToasts() {
  const selectedTopicIds = usePriorityStore((state) => state.selectedTopicIds);
  const isSubmitted = usePriorityStore((state) => state.isSubmitted);
  const maxSelections = usePriorityStore((state) => state.maxSelections);
  const hasToastBeenShown = usePriorityStore((state) => state.hasToastBeenShown);
  const markToastShown = usePriorityStore((state) => state.markToastShown);
  const openModal = usePriorityStore((state) => state.openModal);
  const { t: tCommon } = useTranslation("common");

  const count = selectedTopicIds.length;

  // Track previous count to detect changes
  const prevCountRef = useRef(count);

  useEffect(() => {
    const prevCount = prevCountRef.current;
    prevCountRef.current = count;

    // First star toast - only when going from 0 to 1
    if (count === 1 && prevCount === 0 && !hasToastBeenShown("firstStar")) {
      notifications.show({
        title: tCommon("priorities.toasts.firstStarTitle"),
        message: tCommon("priorities.toasts.firstStarDescription"),
        autoClose: 4000,
      });
      markToastShown("firstStar");
    }

    // All stars used toast - only when reaching max and not already submitted
    if (
      count === maxSelections &&
      prevCount === maxSelections - 1 &&
      !hasToastBeenShown("allStarsUsed") &&
      !isSubmitted
    ) {
      notifications.show({
        title: tCommon("priorities.toasts.allStarsTitle", {
          count: maxSelections,
        }),
        message: (
          <Stack gap="sm">
            <Text size="sm">{tCommon("priorities.toasts.allStarsDescription")}</Text>
            <Button onClick={() => openModal()} size="compact-sm" variant="light" style={{ alignSelf: "flex-start" }}>
              {tCommon("priorities.toasts.allStarsAction")}
            </Button>
          </Stack>
        ),
        autoClose: 8000,
      });
      markToastShown("allStarsUsed");
    }
  }, [count, isSubmitted, tCommon, hasToastBeenShown, markToastShown, openModal, maxSelections]);

  // This component only triggers side effects, renders nothing
  return null;
}

export default PriorityToasts;
